/**
 * Milestone Display Utilities
 * Pure helper functions for milestone rendering logic
 * Extracted from MilestoneMatrixFromAPI component for better testability and reusability
 */

import type { CSSProperties } from 'react';
import {
  ENHANCED_MILESTONE_STATUSES as MILESTONE_STATUSES,
  ENHANCED_STATUS_MENUS as STATUS_MENUS,
  getDateForPastCheck,
  getExcelHoverData
} from './milestoneConfigEnhanced';
import { isMilestoneDateInPast } from '../../../pages/MilestonesGrid/utils/milestoneSortUtils';

/** System-default (API-populated) milestone date — slideout matrix value row */
export const MILESTONE_UNEDITED_DATE_COLOR = '#909090';
/** Past-due milestone date — takes precedence over unedited green */
export const MILESTONE_PAST_DUE_DATE_COLOR = '#D31510';

const MILESTONE_PAST_DUE_EXEMPT_STATUSES = new Set([
  'APPROVED',
  'APPROVED_ASYNC',
  'RE_APPROVED',
  'RE_APPROVED_ASYNC',
  'COMPLETE',
  'COMPLETE_ASYNC',
  'APPROVED_FOLLOW_UP'
]);

export type MilestoneDateStyleInput = {
  isEdited?: boolean;
  status?: string | null;
  date?: string | null;
  secondaryDate?: string | null;
};

/**
 * Whether the milestone's displayed date is in the past (red styling).
 */
export function isMilestonePastDue(
  milestone: MilestoneDateStyleInput
): boolean {
  const statusKey = milestone.status?.toUpperCase() || '';
  if (MILESTONE_PAST_DUE_EXEMPT_STATUSES.has(statusKey)) {
    return false;
  }
  const dateToCheck = getDateForPastCheck(milestone, statusKey);
  return Boolean(dateToCheck && isMilestoneDateInPast(dateToCheck));
}

/**
 * Inline style for milestone value-row date text.
 * Priority: past due (red) → unedited default (gray/italic) → default/inherit (black).
 */
export function getMilestoneDateTextStyle(
  milestone: MilestoneDateStyleInput,
  hasDisplayText: boolean
): CSSProperties {
  if (isMilestonePastDue(milestone)) {
    return { color: MILESTONE_PAST_DUE_DATE_COLOR };
  }
  if (milestone.isEdited === false && hasDisplayText) {
    return {
      color: MILESTONE_UNEDITED_DATE_COLOR,
      fontFamily: "'Adobe Clean', Arial, sans-serif",
      fontStyle: 'italic',
      transform: 'skewX(-8deg)'
    };
  }
  return {};
}

/**
 * Milestone fields from a PATCH response (supports `{ data: { ... } }` envelopes).
 */
export function pickMilestoneFieldsFromPatchResponse(
  result: unknown
): Record<string, unknown> {
  if (!result || typeof result !== 'object') {
    return {};
  }

  const raw = result as Record<string, unknown>;
  const payload =
    raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)
      ? (raw.data as Record<string, unknown>)
      : raw;

  return Object.fromEntries(
    Object.entries(payload).filter(([key, value]) => {
      if (key === 'status' && typeof value === 'number') {
        return false;
      }
      if (key === 'success' || key === 'message' || key === 'errors') {
        return false;
      }
      return true;
    })
  );
}

/**
 * Response-envelope keys `updateMilestoneField`'s PATCH response puts alongside the actual
 * milestone fields (see MilestonesController.java: `responseData.put("issueId"/"milestoneId"/
 * "milestoneName"/"moment"/"notifications", ...)`) — never real milestone fields themselves.
 *
 * Strip these specifically before merging {@link pickMilestoneFieldsFromPatchResponse}'s output
 * into persisted/local milestone state (e.g. the `confirmedMilestone` spread) — NOT from
 * `serverFields` itself, which {@link findEmailSentInPatchResponse} still needs `moment`/
 * `milestoneId`/`milestoneName` on to look up `emailSent`. Left unstripped at the merge point,
 * they round-trip back to the server on the *next* save, where the schema strips them again —
 * surfacing as a confusing "fields not applied" warning instead of never being sent at all.
 */
const PATCH_RESPONSE_ENVELOPE_KEYS = new Set([
  'issueId',
  'milestoneId',
  'milestoneName',
  'moment',
  'notifications',
  'emailSent'
]);

export function stripPatchResponseEnvelopeFields(
  fields: Record<string, unknown>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(fields).filter(
      ([key]) => !PATCH_RESPONSE_ENVELOPE_KEYS.has(key)
    )
  );
}

/**
 * A phase's `milestones` field is an array containing ONE grouping object
 * (`{ milestoneId, milestones: [...] }`), not the individual milestone entries
 * directly — flatten that one level down to the real entries.
 */
const flattenMilestoneGroups = (value: unknown): Record<string, unknown>[] => {
  if (!Array.isArray(value)) return [];
  const flattened: Record<string, unknown>[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const group = item as Record<string, unknown>;
    if (Array.isArray(group.milestones)) {
      flattened.push(...(group.milestones as Record<string, unknown>[]));
    } else if ('name' in group) {
      // Already a flat milestone entry (no extra grouping wrapper).
      flattened.push(group);
    }
  }
  return flattened;
};

/**
 * The flat, single-milestone fields a milestone PATCH response returns (what
 * {@link pickMilestoneFieldsFromPatchResponse} extracts) don't include
 * `emailSent` — it only shows up inside that same response's
 * `moment.releasePhases[].milestones` (phased) or `moment.milestones`
 * (non-phased) collections. The response also gives us `milestoneId` and
 * `milestoneName` at the top level, and each phase carries its own
 * `milestoneId` — use that to find the exact phase (milestone names can repeat
 * across phases with different `emailSent` values), then look up the entry by
 * name within it.
 */
export function findEmailSentInPatchResponse(
  serverFields: Record<string, unknown>
): boolean | undefined {
  const moment = serverFields.moment as Record<string, unknown> | undefined;
  if (!moment) return undefined;

  const targetMilestoneId = serverFields.milestoneId;
  const targetMilestoneName = serverFields.milestoneName;
  if (!targetMilestoneName) return undefined;

  const findByName = (list: Record<string, unknown>[]): boolean | undefined => {
    const match = list.find(entry => entry?.name === targetMilestoneName);
    return typeof match?.emailSent === 'boolean'
      ? (match.emailSent as boolean)
      : undefined;
  };

  const phases = moment.releasePhases;
  if (Array.isArray(phases)) {
    const phase = targetMilestoneId
      ? phases.find((p: any) => p && p.milestoneId === targetMilestoneId)
      : undefined;
    if (phase) {
      const found = findByName(
        flattenMilestoneGroups((phase as any).milestones)
      );
      if (found !== undefined) return found;
    }
    // Fallback: the milestoneId didn't match any phase — scan them all.
    for (const p of phases) {
      const found = findByName(flattenMilestoneGroups((p as any)?.milestones));
      if (found !== undefined) return found;
    }
  }

  // Non-phased moments: milestones live directly on the moment.
  const rootMilestones = moment.milestones;
  if (rootMilestones) {
    const list = Array.isArray(rootMilestones)
      ? flattenMilestoneGroups(rootMilestones)
      : Array.isArray((rootMilestones as any)?.milestones)
      ? ((rootMilestones as any).milestones as Record<string, unknown>[])
      : [];
    const found = findByName(list);
    if (found !== undefined) return found;
  }

  return undefined;
}

/**
 * Infer review type from status
 * Determines the review type (Meeting/Async/etc.) based on status value
 *
 * @param status - Milestone status
 * @returns Inferred review type or empty string
 */
export function inferTypeOfReviewFromStatus(status?: string): string {
  if (!status) return '';

  // Safely convert to uppercase for lookup only
  const statusStr = typeof status === 'string' ? status : String(status);
  const statusUpper = statusStr.toUpperCase();

  // Check against enhanced status menus to determine the type
  for (const [reviewType, statuses] of Object.entries(STATUS_MENUS)) {
    if (statuses.includes(statusUpper)) {
      return reviewType;
    }
  }

  // Fallback logic based on status patterns
  if (statusUpper.includes('MEETING') || statusUpper.includes('SCHEDULED')) {
    return 'Meeting';
  }
  if (statusUpper.includes('ASYNC') || statusUpper.includes('SENT')) {
    return 'Async';
  }

  return '';
}

/**
 * Get cell width class for milestone name
 * Determines CSS width class based on milestone name length and specific names
 *
 * @param milestoneName - Name of the milestone
 * @returns CSS class name for width
 */
export function getCellWidthClass(milestoneName: string): string {
  const name = milestoneName.toUpperCase();

  // Based on original working logic - updated to handle Creative Arch properly
  if (
    name.length > 12 ||
    [
      'MEDIA BRAINSTORM',
      'CREATIVE ARCH',
      'MEDIA BRNSTRM',
      'CREATV ARCH'
    ].includes(name)
  ) {
    return name.length > 15 || name === 'MKTG PKG' ? 'wide' : 'wide';
  }
  return 'wide';
}

/**
 * Get descriptive field label for milestone UI (status / review context).
 *
 * @param fieldId - Field identifier
 * @param status - Milestone status
 * @param reviewType - Review type
 * @returns Descriptive label in uppercase
 */
export function getDescriptiveFieldLabel(
  fieldId: string,
  status: string,
  reviewType: string
): string {
  // Safely convert to uppercase for config lookup only
  const statusStr = typeof status === 'string' ? status : String(status);
  const statusKey = statusStr.toUpperCase();
  const enhancedStatusConfig = MILESTONE_STATUSES[statusKey];

  // Handle date field labels based on status and review type
  if (fieldId === 'date') {
    // First check combination-specific overrides from getExcelHoverData
    // Determine approval condition based on status configuration
    const approvalCondition = enhancedStatusConfig?.showApprover
      ? 'APPROVAL_REQUIRED'
      : 'NO_APPROVAL_REQUIRED';

    try {
      const hoverData = getExcelHoverData(
        approvalCondition,
        reviewType,
        status
      );
      if (hoverData?.rolloverCopy) {
        return hoverData.rolloverCopy.toUpperCase();
      }
    } catch (error) {
      // If getExcelHoverData fails, continue to fallback logic
    }

    // Second check enhanced configuration for rolloverCopy
    if (enhancedStatusConfig?.rolloverCopy) {
      return enhancedStatusConfig.rolloverCopy.toUpperCase();
    }

    // Fallback to legacy logic for backward compatibility
    if (statusKey?.includes('APPROVED')) {
      const baseLabel =
        enhancedStatusConfig?.historicalFields?.dateLabel || 'APPROVED ON';
      return baseLabel;
    } else if (statusKey === 'MEETING_SCHEDULED') {
      return 'MEETING SCHEDULED ON';
    } else if (statusKey === 'SENT') {
      return 'SENT ON';
    } else if (statusKey === 'TARGET') {
      // Special case: Meeting + Target = "MEETING TARGET"
      if (reviewType?.toLowerCase().includes('meeting')) {
        return 'MEETING TARGET';
      } else {
        return 'TARGET DATE';
      }
    } else {
      return 'DATE';
    }
  }

  // ✅ Handle secondaryDate field labels based on status and review type
  if (fieldId === 'secondaryDate') {
    // First check combination-specific overrides from getExcelHoverData
    const approvalCondition = enhancedStatusConfig?.showApprover
      ? 'APPROVAL_REQUIRED'
      : 'NO_APPROVAL_REQUIRED';

    try {
      const hoverData = getExcelHoverData(
        approvalCondition,
        reviewType,
        status
      );
      // Extract secondary date label from rolloverCopy if it exists
      if (hoverData?.rolloverCopy) {
        const rolloverText = hoverData.rolloverCopy;
        // Look for secondary date patterns in rollover text
        if (rolloverText.toLowerCase().includes('follow-up on')) {
          return 'FOLLOW-UP ON';
        } else if (rolloverText.toLowerCase().includes('due on')) {
          return 'DUE ON';
        } else if (rolloverText.toLowerCase().includes('pending until')) {
          return 'PENDING UNTIL';
        } else if (rolloverText.toLowerCase().includes('original date')) {
          return 'ORIGINAL DATE';
        }
      }
    } catch (error) {
      // If getExcelHoverData fails, continue to fallback logic
    }

    // Fallback logic based on status patterns
    if (statusKey?.includes('APPROVED')) {
      return 'FOLLOW-UP ON';
    } else if (statusKey?.includes('SENT')) {
      // SENT or SENT_PENDING
      if (statusKey.includes('PENDING')) {
        return 'PENDING UNTIL';
      }
      return 'DUE ON';
    } else if (
      statusKey?.includes('COMPLETE') &&
      statusKey.includes('ORIGINAL')
    ) {
      return 'ORIGINAL DATE';
    } else if (statusKey?.includes('PENDING')) {
      return 'PENDING UNTIL';
    }

    // Generic fallback
    return 'SECONDARY DATE';
  }

  // Handle meeting scheduled fields
  if (fieldId === 'meetingScheduledOn') {
    return 'MEETING SCHEDULED ON';
  }

  if (fieldId === 'meetingScheduledAt') {
    return 'MEETING SCHEDULED AT';
  }

  // For other fields, return title case version
  return (
    fieldId.charAt(0).toUpperCase() +
    fieldId
      .slice(1)
      .replace(/([A-Z])/g, ' $1')
      .trim()
  );
}
